<script lang="ts">
  /**
   * One game in the Zonenchat (amendment 19), drawn in place of its message's text: the
   * Miesmuschel's answer, or an invitation that turns into a board once somebody takes it.
   *
   * What it shows is the server's view of the game, which already leaves out what this person
   * may not see — the other hand in an unfinished round. A tap sends the move and draws the
   * answer at once; the other player's moves arrive with the chat's polling.
   */
  import { untrack } from 'svelte';
  import { DUEL_NAMES, HANDS, type Hand, roundWinner, type Seat } from '$domain/games.ts';
  import { describeError } from './session.svelte.ts';
  import { isDuelView, type Move, type ZoneMessage, zone } from './zone.svelte.ts';

  type Props = { message: ZoneMessage };
  const { message }: Props = $props();

  const HAND_SIGNS: Record<Hand, string> = { rock: '✊', paper: '✋', scissors: '✌️' };
  const HAND_NAMES: Record<Hand, string> = { rock: 'Stein', paper: 'Papier', scissors: 'Schere' };
  /** An answer this young was asked just now: the Miesmuschel takes a moment before it speaks. */
  const FRESH_MS = 30_000;
  /** How long "Wirklich aufgeben?" waits for the second tap. */
  const RESIGN_MS = 4_000;

  let busy = $state(false);
  let error = $state('');
  let resigning = $state(false);
  let resignTimer: ReturnType<typeof setTimeout> | undefined;

  // Read once: the animation is for an answer arriving, not for one scrolled past later.
  const fresh = untrack(() => Date.now() - Date.parse(message.createdAt) < FRESH_MS);

  const game = $derived(message.game);
  const duel = $derived(isDuelView(game) ? game : undefined);

  const otherSeat = (seat: Seat): Seat => (seat === 'host' ? 'guest' : 'host');

  /** A player as the card names them; a guest whose account went has no name left. */
  function handle(seat: Seat): string {
    if (seat === 'host') return `@${message.author.username}`;
    return duel?.guest ? `@${duel.guest.username}` : 'ein gelöschtes Konto';
  }

  /** "Du" for the one reading, the @name for anybody else. */
  const nameOf = (seat: Seat) => (duel?.you === seat ? 'Du' : handle(seat));

  /** Whether the game waits for this person: their square to set, their hand to choose. */
  const waitsForYou = $derived(
    duel !== undefined &&
      duel.status === 'running' &&
      duel.you !== null &&
      (duel.kind === 'tictactoe' ? duel.turn === duel.you : duel.mine === null),
  );

  const status = $derived.by(() => {
    if (duel === undefined) return '';
    const you = duel.you;
    switch (duel.status) {
      case 'open':
        return message.mine ? 'Warte auf jemanden, der mitspielt …' : 'Wer zuerst tippt, spielt.';
      case 'expired':
        return 'Niemand hat mitgespielt.';
      case 'asleep':
        return 'Eingeschlafen – zehn Minuten ohne Zug.';
      case 'running': {
        if (duel.kind === 'tictactoe') {
          if (duel.turn === null) return '';
          return duel.turn === you ? 'Du bist dran.' : `${handle(duel.turn)} ist dran.`;
        }
        const last = duel.rounds.at(-1);
        const again = last !== undefined && last.host === last.guest ? 'Gleichstand – noch mal! ' : '';
        if (you === null) return `${again}Beide wählen …`;
        const them = otherSeat(you);
        if (duel.mine !== null) return `Warte auf ${handle(them)} …`;
        // Whether, never what: the other hand stays on the server until this one is chosen.
        return duel.chosen[them]
          ? `${again}Wähl deine Hand – ${handle(them)} hat schon gewählt.`
          : `${again}Wähl deine Hand.`;
      }
      case 'over': {
        const { winner, resigned } = duel;
        if (winner === null) return 'Unentschieden.';
        const gave =
          resigned === null
            ? ''
            : resigned === you
              ? 'Du hast aufgegeben. '
              : `${handle(resigned)} hat aufgegeben. `;
        return winner === you ? `${gave}Gewonnen! 🎉` : `${gave}${handle(winner)} gewinnt.`;
      }
    }
  });

  async function run(action: () => Promise<void>) {
    busy = true;
    error = '';
    try {
      await action();
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  const join = () => run(() => zone.join(message.id));
  const play = (move: Move) => run(() => zone.move(message.id, move));

  /** Two taps: a game is too easily lost to a thumb on the way to the board. */
  function resign() {
    clearTimeout(resignTimer);
    if (!resigning) {
      resigning = true;
      resignTimer = setTimeout(() => (resigning = false), RESIGN_MS);
      return;
    }
    resigning = false;
    void play({ resign: true });
  }
</script>

{#if game?.kind === 'conch'}
  <div class="card conch" class:fresh>
    <p class="asks">fragt die magische Miesmuschel:</p>
    <p class="question">„{message.body}“</p>
    <p class="answer">
      <span class="shell" aria-hidden="true">🐚</span><span class="said">{game.answer}</span>
    </p>
  </div>
{:else if duel !== undefined}
  <div class="card duel">
    <p class="title">
      <span aria-hidden="true">{duel.kind === 'tictactoe' ? '❌⭕' : '✊✋✌️'}</span>
      {DUEL_NAMES[duel.kind]}
    </p>

    {#if duel.status === 'open' || duel.status === 'expired'}
      <!-- Nothing to draw yet: the status line says what is going on. -->
    {:else if duel.kind === 'tictactoe'}
      <p class="players">
        <span class="mark x" aria-hidden="true">✕</span>
        {nameOf('guest')} gegen <span class="mark o" aria-hidden="true">◯</span>
        {nameOf('host')}
      </p>
      <div class="board">
        {#each [...duel.board] as square, index (index)}
          <button
            class="square"
            class:x={square === 'x'}
            class:o={square === 'o'}
            class:line={duel.line?.includes(index)}
            disabled={busy || square !== '.' || !waitsForYou}
            aria-label={`Feld ${index + 1}${square === 'x' ? ', Kreuz' : square === 'o' ? ', Kreis' : ''}`}
            onclick={() => play({ square: index })}
          >
            {#if square !== '.'}<span aria-hidden="true">{square === 'x' ? '✕' : '◯'}</span>{/if}
          </button>
        {/each}
      </div>
    {:else}
      <p class="players score">
        {nameOf('host')}
        <strong>{duel.score.host} : {duel.score.guest}</strong>
        {nameOf('guest')}
      </p>
      {#if duel.rounds.length > 0}
        <ol class="rounds" aria-label="Runden">
          {#each duel.rounds as round, index (index)}
            {@const winner = roundWinner(round)}
            <li class:tie={winner === null}>
              <span class:won={winner === 'host'}>{HAND_SIGNS[round.host]}</span>
              <span class="colon">:</span>
              <span class:won={winner === 'guest'}>{HAND_SIGNS[round.guest]}</span>
            </li>
          {/each}
        </ol>
      {/if}
      {#if duel.status === 'running'}
        {#if waitsForYou}
          <div class="hands">
            {#each HANDS as hand (hand)}
              <button disabled={busy} onclick={() => play({ hand })}>
                <span aria-hidden="true">{HAND_SIGNS[hand]}</span>{HAND_NAMES[hand]}
              </button>
            {/each}
          </div>
        {:else if duel.you !== null && duel.mine !== null}
          <p class="chosen">Du hast {HAND_SIGNS[duel.mine]} {HAND_NAMES[duel.mine]} gewählt.</p>
        {:else if duel.you === null}
          <p class="chosen">
            {handle('host')}
            {duel.chosen.host ? '✓' : '…'} · {handle('guest')}
            {duel.chosen.guest ? '✓' : '…'}
          </p>
        {/if}
      {/if}
    {/if}

    {#if status}
      <p
        class="state"
        class:act={waitsForYou || (duel.status === 'over' && duel.you !== null)}
        role="status"
      >
        {status}
      </p>
    {/if}

    {#if duel.status === 'open' && !message.mine}
      <button class="join" onclick={join} disabled={busy}>Mitspielen</button>
    {/if}
    {#if duel.status === 'running' && duel.you !== null}
      <button class="resign" class:sure={resigning} onclick={resign} disabled={busy}>
        {resigning ? 'Wirklich aufgeben?' : 'Aufgeben'}
      </button>
    {/if}
    {#if error}<p class="error" role="status">{error}</p>{/if}
  </div>
{/if}

<style>
  .card {
    --cross: var(--accent);
    --nought: #3d8bfd;
    margin-top: 0.3rem;
    padding: 0.6rem 0.75rem 0.65rem;
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    background: color-mix(in srgb, var(--accent) 5%, var(--card));
  }

  p {
    margin: 0;
  }

  .title {
    font-size: 0.9375rem;
    font-weight: 600;
  }

  .players {
    margin-top: 0.3rem;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .mark {
    font-weight: 700;
  }

  .mark.x,
  .square.x {
    color: var(--cross);
  }

  .mark.o,
  .square.o {
    color: var(--nought);
  }

  .board {
    display: grid;
    grid-template-columns: repeat(3, 3.25rem);
    gap: 0.3rem;
    justify-content: center;
    margin: 0.6rem 0 0.2rem;
  }

  .square {
    display: grid;
    place-items: center;
    aspect-ratio: 1;
    padding: 0;
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1;
  }

  .square:not(:disabled) {
    border-color: color-mix(in srgb, var(--accent) 45%, var(--border));
    cursor: pointer;
  }

  .square:not(:disabled):hover {
    background: color-mix(in srgb, var(--accent) 10%, var(--bg));
  }

  .square.line {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 22%, var(--bg));
  }

  .square span {
    animation: drop 220ms ease-out;
  }

  .score strong {
    margin: 0 0.35rem;
    color: var(--fg);
    font-size: 1rem;
  }

  .rounds {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 0.75rem;
    margin: 0.4rem 0 0;
    padding: 0;
    list-style: none;
    font-size: 1.15rem;
  }

  .rounds li.tie {
    opacity: 0.5;
  }

  .rounds .colon {
    color: var(--muted);
    font-size: 0.875rem;
  }

  .rounds .won {
    display: inline-block;
    border-radius: 50%;
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 70%, transparent);
  }

  .hands {
    display: flex;
    gap: 0.4rem;
    margin-top: 0.5rem;
  }

  .hands button {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    padding: 0.45rem 0.2rem;
    border: 1px solid color-mix(in srgb, var(--accent) 45%, var(--border));
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.75rem;
    cursor: pointer;
  }

  .hands button:hover {
    background: color-mix(in srgb, var(--accent) 10%, var(--bg));
  }

  .hands span {
    font-size: 1.6rem;
    line-height: 1;
  }

  .chosen {
    margin-top: 0.4rem;
    font-size: 0.8125rem;
  }

  .state {
    margin-top: 0.35rem;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .state.act {
    color: var(--fg);
    font-weight: 600;
  }

  .join {
    margin-top: 0.45rem;
    padding: 0.45rem 1.1rem;
    border: 1px solid var(--accent);
    border-radius: 0.6rem;
    background: color-mix(in srgb, var(--accent) 16%, var(--bg));
    color: var(--fg);
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
  }

  .resign {
    margin-top: 0.3rem;
    padding: 0.1rem 0;
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 0.75rem;
    cursor: pointer;
  }

  .resign.sure {
    color: var(--accent);
    font-weight: 600;
  }

  .error {
    margin-top: 0.3rem;
    font-size: 0.8125rem;
    color: var(--accent);
  }

  .asks {
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .question {
    margin-top: 0.15rem;
    font-size: 0.9375rem;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .answer {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-top: 0.4rem;
    font-size: 1.05rem;
    font-style: italic;
    font-weight: 600;
  }

  .shell {
    display: inline-block;
    font-size: 1.5rem;
    font-style: normal;
  }

  .said {
    display: inline-block;
  }

  /* Asked just now: the shell wobbles, and then it speaks. */
  .fresh .shell {
    animation: wobble 0.9s ease-in-out;
  }

  .fresh .said {
    animation: speak 0.45s ease-out 0.9s both;
  }

  @keyframes drop {
    from {
      opacity: 0;
      transform: scale(0.4);
    }
  }

  @keyframes wobble {
    20% {
      transform: rotate(-18deg);
    }
    40% {
      transform: rotate(14deg);
    }
    60% {
      transform: rotate(-10deg);
    }
    80% {
      transform: rotate(6deg);
    }
  }

  @keyframes speak {
    from {
      opacity: 0;
      transform: translateY(4px) scale(0.92);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .square span,
    .fresh .shell,
    .fresh .said {
      animation: none;
    }
  }
</style>
